import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown } from 'lucide-react';

/** Two-way Email/Channel switch from the authoritative Console Templates UI. */
export function SegmentedToggle({ value, onChange, options, disabled, disabledHint }) {
    return (
        <div style={styles.segmented} role="tablist" aria-label="Template kind">
            {options.map(opt => {
                const active = opt.value === value;
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        disabled={disabled && !active}
                        title={disabled && !active ? disabledHint : undefined}
                        onClick={() => !disabled && onChange(opt.value)}
                        style={{
                            ...styles.segmentedBtn,
                            ...(active ? styles.segmentedBtnActive : {}),
                            ...(disabled && !active ? styles.segmentedBtnDisabled : {}),
                        }}
                    >
                        {opt.icon ? <opt.icon size={14} style={{ marginRight: 6 }} /> : null}
                        {opt.label}
                    </button>
                );
            })}
        </div>
    );
}

/** Insert a variable token at the target input's current cursor position. */
export function InsertVariableButton({ targetRef, formField, setForm, variables }) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [open]);

    const insert = (token) => {
        const field = targetRef.current;
        if (!field) {
            setForm(form => ({ ...form, [formField]: form[formField] + token }));
            setOpen(false);
            return;
        }

        const start = field.selectionStart ?? field.value.length;
        const end = field.selectionEnd ?? start;
        const next = field.value.substring(0, start) + token + field.value.substring(end);
        setForm(form => ({ ...form, [formField]: next }));
        requestAnimationFrame(() => {
            field.focus();
            const position = start + token.length;
            field.setSelectionRange(position, position);
        });
        setOpen(false);
    };

    return (
        <div ref={dropdownRef} style={{ position: 'relative', display: 'inline-block' }}>
            <button type="button" onClick={() => setOpen(value => !value)} style={styles.insertBtn} title="Insert a template variable at cursor">
                <span style={{ fontSize: 12 }}>{'{{ }}'}</span>
                <span style={{ marginLeft: 4, fontSize: 12 }}>Insert Variable</span>
                <ChevronDown size={12} style={{ marginLeft: 4, opacity: 0.6 }} />
            </button>
            {open && (
                <div style={styles.dropdown}>
                    {variables.map(variable => (
                        <button
                            type="button"
                            key={variable.token}
                            onClick={() => insert(variable.token)}
                            style={styles.dropdownItem}
                            onMouseEnter={event => { event.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                            onMouseLeave={event => { event.currentTarget.style.background = 'transparent'; }}
                        >
                            <code style={styles.varToken}>{variable.token}</code>
                            <span style={styles.varDesc}>{variable.description}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// These values are deliberately the same literal dark-panel presentation used
// by Console Account Templates. Channels uses the same data-panel language.
export const styles = {
    header: { marginBottom: 20 },
    title: { margin: 0, fontSize: 22, fontWeight: 700, color: '#F1F5F9', letterSpacing: '-0.02em' },
    subtitle: { margin: '6px 0 0', fontSize: 14, color: '#64748B' },
    commandRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16 },
    error: { padding: '10px 16px', background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, color: '#FCA5A5', fontSize: 13, marginBottom: 16 },
    notice: { padding: '10px 16px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 8, color: '#93C5FD', fontSize: 13, marginBottom: 16 },
    empty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', background: 'rgba(255,255,255,0.02)', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 12, textAlign: 'center' },
    addBtn: { display: 'flex', alignItems: 'center', padding: '8px 16px', background: '#3B82F6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    list: { display: 'flex', flexDirection: 'column', gap: 8 },
    card: { display: 'flex', alignItems: 'center', padding: '14px 18px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10 },
    cardActions: { display: 'flex', gap: 6, marginLeft: 12 },
    iconBtn: { background: 'transparent', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, color: '#94A3B8', cursor: 'pointer', padding: '6px 8px', display: 'flex', alignItems: 'center' },
    typeBadge: { display: 'inline-block', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600, background: 'rgba(99,102,241,0.15)', color: '#818CF8', textTransform: 'capitalize' },
    cardTitle: { color: '#F1F5F9', fontSize: 14 },
    cardBody: { color: '#64748B', fontSize: 12, margin: 0, lineHeight: 1.4, maxHeight: 38, overflow: 'hidden' },
    cardBodyUnset: { color: '#64748B', fontSize: 12, margin: 0, lineHeight: 1.4, fontStyle: 'italic' },
    formCard: { padding: '28px 32px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12 },
    formTitle: { margin: '0 0 20px', color: '#F1F5F9', fontSize: 16 },
    fieldGroup: { marginBottom: 18 },
    label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#CBD5E1', marginBottom: 6 },
    input: { width: '100%', maxWidth: 540, padding: '10px 14px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#F1F5F9', fontSize: 14, outline: 'none', boxSizing: 'border-box' },
    hint: { display: 'block', marginTop: 6, fontSize: 11, color: '#64748B' },
    resetBtn: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', background: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6, color: '#94A3B8', cursor: 'pointer', fontSize: 12, fontWeight: 500 },
    preview: { marginTop: 4, marginBottom: 18, padding: '16px 18px', maxWidth: 540, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, boxSizing: 'border-box' },
    previewHeader: { display: 'flex', alignItems: 'center', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748B', marginBottom: 12 },
    previewSubject: { fontSize: 14, fontWeight: 600, color: '#F1F5F9', paddingBottom: 10, borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 12 },
    previewPartLabel: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', margin: '10px 0 6px' },
    previewHtml: { background: '#FFFFFF', color: '#202124', borderRadius: 6, padding: '14px 16px', fontSize: 14, overflowWrap: 'anywhere' },
    previewText: { margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontFamily: 'monospace', fontSize: 12.5, lineHeight: 1.55, color: '#CBD5E1' },
    previewNote: { marginTop: 10, fontSize: 11, color: '#F59E0B' },
    chatBubble: { background: '#1E3A5F', color: '#E8EEF7', borderRadius: '14px 14px 14px 4px', padding: '10px 14px', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', maxWidth: 420 },
    chatBubbleEmpty: { background: 'rgba(255,255,255,0.03)', color: '#64748B', borderRadius: 10, padding: '10px 14px', fontSize: 13, fontStyle: 'italic', border: '1px dashed rgba(255,255,255,0.08)' },
    insertBtn: { display: 'inline-flex', alignItems: 'center', padding: '4px 10px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 6, color: '#A5B4FC', cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 0.15s' },
    dropdown: { position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 100, background: '#1E293B', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,0.4)', padding: '6px 0', minWidth: 320, maxHeight: 280, overflowY: 'auto' },
    dropdownItem: { display: 'flex', flexDirection: 'column', width: '100%', textAlign: 'left', padding: '8px 14px', border: 'none', background: 'transparent', cursor: 'pointer', transition: 'background 0.1s' },
    varToken: { fontSize: 13, fontWeight: 600, color: '#818CF8', fontFamily: 'monospace' },
    varDesc: { fontSize: 11, color: '#94A3B8', marginTop: 2, lineHeight: 1.3 },
    saveBtn: { display: 'flex', alignItems: 'center', padding: '10px 20px', background: '#3B82F6', border: 'none', borderRadius: 8, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
    cancelBtn: { display: 'flex', alignItems: 'center', padding: '10px 20px', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#94A3B8', fontSize: 14, cursor: 'pointer' },
    btnDisabled: { opacity: 0.5, cursor: 'not-allowed' },
    segmented: { display: 'inline-flex', padding: 3, gap: 2, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10 },
    segmentedBtn: { display: 'inline-flex', alignItems: 'center', padding: '6px 14px', background: 'transparent', border: 'none', borderRadius: 7, color: '#94A3B8', fontSize: 13, fontWeight: 600, cursor: 'pointer', transition: 'background 0.15s, color 0.15s' },
    segmentedBtnActive: { background: '#3B82F6', color: '#fff' },
    segmentedBtnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
};
